import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageSquare, Plus, Copy, Trash2, ChevronUp, ChevronDown, MoreVertical, Pencil, Check, Send,
} from "lucide-react";

const uid = () => "id-" + Math.random().toString(36).slice(2, 10);

export default function MessageTemplates() {
  const [sections, setSections] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const saveTimer = useRef(null);
  const skipNext = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/message-templates");
      skipNext.current = true;
      setSections(data.sections || []);
      setLoaded(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Debounced autosave whenever sections change (except on initial load)
  useEffect(() => {
    if (!loaded) return;
    if (skipNext.current) { skipNext.current = false; return; }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.put("/message-templates", { sections }); }
      catch (e) { toast.error(formatApiError(e)); }
    }, 500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [sections, loaded]);

  const swap = (arr, i, j) => {
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr]; [next[i], next[j]] = [next[j], next[i]]; return next;
  };

  const addSection = () => setSections((s) => [
    ...s, { id: uid(), title: "New heading", messages: [{ id: uid(), body: "" }] },
  ]);
  const renameSection = (id, title) =>
    setSections((s) => s.map((x) => x.id === id ? { ...x, title } : x));
  const deleteSection = (id) => {
    if (!window.confirm("Delete this heading and all its messages?")) return;
    setSections((s) => s.filter((x) => x.id !== id));
  };
  const moveSection = (id, dir) => setSections((s) => {
    const i = s.findIndex((x) => x.id === id); if (i === -1) return s;
    return swap(s, i, i + dir);
  });

  const addMessage = (sid) => setSections((s) => s.map(
    (x) => x.id === sid ? { ...x, messages: [...x.messages, { id: uid(), body: "" }] } : x
  ));
  const editMessage = (sid, mid, body) => setSections((s) => s.map(
    (x) => x.id === sid
      ? { ...x, messages: x.messages.map((m) => m.id === mid ? { ...m, body } : m) }
      : x
  ));
  const deleteMessage = (sid, mid) => {
    if (!window.confirm("Delete this message?")) return;
    setSections((s) => s.map(
      (x) => x.id === sid ? { ...x, messages: x.messages.filter((m) => m.id !== mid) } : x
    ));
  };
  const moveMessage = (sid, mid, dir) => setSections((s) => s.map((x) => {
    if (x.id !== sid) return x;
    const i = x.messages.findIndex((m) => m.id === mid); if (i === -1) return x;
    return { ...x, messages: swap(x.messages, i, i + dir) };
  }));

  const copyMessage = async (body) => {
    if (!body || !body.trim()) { toast.error("Message is empty"); return; }
    try { await navigator.clipboard.writeText(body); toast.success("Copied"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const sendOnWhatsApp = (body) => {
    if (!body || !body.trim()) { toast.error("Message is empty"); return; }
    // whatsapp:// URL scheme launches the native WhatsApp desktop/mobile app
    // and opens the contact picker with the text pre-filled.
    const url = `whatsapp://send?text=${encodeURIComponent(body)}`;
    const a = document.createElement("a");
    a.href = url;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="px-6 lg:px-10 pt-4 lg:pt-6 pb-3 max-w-[1000px] mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center">
          <MessageSquare size={18} />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Saved patient messages</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">Message Templates</h1>
        </div>
        <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white"
                onClick={addSection} data-testid="add-section-button">
          <Plus size={14} className="mr-1" /> New heading
        </Button>
      </div>

      <div className="mt-4 space-y-4" data-testid="templates-list">
        {sections.length === 0 && loaded && (
          <Card className="p-8 text-center text-sm text-slate-500 border-dashed">
            No headings yet. Click <span className="font-medium text-slate-800">New heading</span> to add your first message template.
          </Card>
        )}
        {sections.map((sec, idx) => (
          <Card key={sec.id} className="border-slate-200 p-4" data-testid={`section-${sec.id}`}>
            <div className="flex items-center gap-2">
              {editingTitleId === sec.id ? (
                <Input
                  autoFocus
                  value={sec.title}
                  onChange={(e) => renameSection(sec.id, e.target.value)}
                  onBlur={() => setEditingTitleId(null)}
                  onKeyDown={(e) => { if (e.key === "Enter") setEditingTitleId(null); }}
                  className="text-lg font-semibold"
                  data-testid={`section-title-input-${sec.id}`}
                />
              ) : (
                <button type="button"
                        onClick={() => setEditingTitleId(sec.id)}
                        className="text-lg font-semibold text-slate-900 hover:text-sky-700 flex items-center gap-1"
                        data-testid={`section-title-${sec.id}`}>
                  {sec.title || <span className="italic text-slate-400">(untitled)</span>}
                  <Pencil size={12} className="text-slate-300" />
                </button>
              )}
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                        disabled={idx === 0}
                        onClick={() => moveSection(sec.id, -1)}
                        aria-label="Move heading up"
                        data-testid={`section-up-${sec.id}`}>
                  <ChevronUp size={14} />
                </Button>
                <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                        disabled={idx === sections.length - 1}
                        onClick={() => moveSection(sec.id, 1)}
                        aria-label="Move heading down"
                        data-testid={`section-down-${sec.id}`}>
                  <ChevronDown size={14} />
                </Button>
                <Button size="sm" variant="outline"
                        className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => deleteSection(sec.id)}
                        aria-label="Delete heading"
                        data-testid={`section-delete-${sec.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {sec.messages.length === 0 && (
                <div className="text-xs text-slate-400 italic px-1">No messages under this heading yet.</div>
              )}
              {sec.messages.map((m, mi) => (
                <div key={m.id} className="rounded-md border border-slate-200 bg-slate-50/40 p-2"
                     data-testid={`message-${m.id}`}>
                  <Textarea
                    value={m.body}
                    onChange={(e) => editMessage(sec.id, m.id, e.target.value)}
                    rows={Math.min(6, Math.max(2, (m.body.match(/\n/g) || []).length + 2))}
                    placeholder="Type the message template — use tokens like <Name>, <Date>, <Time>."
                    data-testid={`message-body-${m.id}`}
                  />
                  <div className="mt-2 flex items-center gap-1">
                    <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white h-8"
                            onClick={() => copyMessage(m.body)}
                            data-testid={`message-copy-${m.id}`}>
                      <Copy size={13} className="mr-1" /> Copy
                    </Button>
                    <Button size="sm" className="bg-[#25D366] hover:bg-[#1EBE5A] text-white h-8"
                            onClick={() => sendOnWhatsApp(m.body)}
                            data-testid={`message-whatsapp-${m.id}`}
                            title="Open WhatsApp with this message pre-filled">
                      <Send size={13} className="mr-1" /> WhatsApp
                    </Button>
                    <div className="ml-auto flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                              disabled={mi === 0}
                              onClick={() => moveMessage(sec.id, m.id, -1)}
                              aria-label="Move message up"
                              data-testid={`message-up-${m.id}`}>
                        <ChevronUp size={13} />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                              disabled={mi === sec.messages.length - 1}
                              onClick={() => moveMessage(sec.id, m.id, 1)}
                              aria-label="Move message down"
                              data-testid={`message-down-${m.id}`}>
                        <ChevronDown size={13} />
                      </Button>
                      <Button size="sm" variant="outline"
                              className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => deleteMessage(sec.id, m.id)}
                              aria-label="Delete message"
                              data-testid={`message-delete-${m.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline"
                      onClick={() => addMessage(sec.id)}
                      className="h-8"
                      data-testid={`add-message-${sec.id}`}>
                <Plus size={13} className="mr-1" /> Add message
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// suppress unused-icon warnings for MoreVertical/Check reserved for future inline actions
void MoreVertical; void Check;
